import React, { useState, useEffect } from 'react';
import AuthService from '../services/AuthService';
import { getDefaultAdminRoute } from '../utils/permissions';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Loader from '../components/Loader';
import logo from '../assets/images/logo-dark.png';
import './Login.css';

const slides = [
  {
    title: 'Manage Your Business',
    description: 'Bookings, clients, memberships and payments — everything that keeps Boost Area running, in one place.'
  },
  {
    title: 'Bookings at a Glance',
    description: 'See every session on the calendar, add walk-ins in seconds, and keep each room running on time.'
  },
  {
    title: 'Know Your Numbers',
    description: 'Track revenue, new members and busy hours with live insights, wherever you are.'
  }
];

const AdminLogin = () => {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoader, setIsLoader] = useState(false);
  const [requiresOTP, setRequiresOTP] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (AuthService.getCurrentUser()) {
      navigate(getDefaultAdminRoute());
    }
  }, [navigate]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoader(true);
    try {
      const data = await AuthService.login(login.trim(), password, otp);
      if (data.status) {
        toast.success(data.message);
        let user = data.data.admin;
        user.auth_token = data.data.auth_token;
        // An admin with no role has full access; the panel reads this flag
        // rather than treating an empty permission list as "everything".
        user.is_super_admin = Boolean(data.data.is_super_admin);
        // Lets the panel tell an "Admin"-role account apart from Staff — e.g.
        // the mandatory Check In gate only applies to Staff.
        user.role_name = data.data.role_name || null;
        localStorage.setItem('admin', JSON.stringify(user));
        localStorage.setItem('permissions', JSON.stringify(data.data.permissions || []));
        // Dashboard is restricted — a Staff role without it lands on the
        // Booking Calendar instead.
        window.location.href = process.env.REACT_APP_BASE_PATH + getDefaultAdminRoute();
      } else {
        if (data.requires_otp) {
          setRequiresOTP(true);
          toast.info(data.message || 'OTP sent to your email');
        } else {
          toast.error(data.message);
        }
      }
    } catch (error) {
      console.error('Login failed:', error);
      toast.error('Login failed. Please try again.');
    } finally {
      setIsLoader(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-left-panel">
        <div className="left-panel-content">
          <div className="slider-container">
            <div
              className="slider-wrapper"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {slides.map((slide, index) => (
                <div key={index} className="slide">
                  <h1 className="left-panel-title">{slide.title}</h1>
                  <p className="left-panel-description">{slide.description}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="navigation-dots">
            {slides.map((_, index) => (
              <span
                key={index}
                className={`dot ${index === currentSlide ? 'active' : ''}`}
                onClick={() => setCurrentSlide(index)}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="login-right-panel">
        <div className="login-form-container">
          {/* Phones: a dark brand hero above the form sheet (Login.css). */}
          <div className="login-mobile-hero">
            <img src={logo} alt="Boost Area" className="login-mobile-logo" />
            <div className="login-mobile-brand">Boost Area</div>
            <div className="login-mobile-tagline">Business Portal</div>
          </div>

          <div className="login-form-content">
            <div className="logo-section">
              <img src={logo} alt="Logo" className="logo-image w-100px" />
            </div>

            <div className="login-heading">
              <h2 className="login-title">Welcome back</h2>
              <p className="login-subtitle">Login to manage your business — bookings, clients and memberships.</p>
            </div>

            <form onSubmit={handleLogin} className="login-form">
              <div className="form-group">
                <label className="form-label-sm" htmlFor="login-email">Username or Email</label>
                <div className="input-with-icon">
                  <i className="bi bi-person"></i>
                  <input
                    id="login-email"
                    type="text"
                    required
                    autoComplete="username"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    value={login}
                    onChange={(e) => setLogin(e.target.value)}
                    className="form-input"
                    placeholder="Username or you@business.com"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label-sm" htmlFor="login-password">Password</label>
                <div className="input-with-icon">
                  <i className="bi bi-lock"></i>
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    className="form-input"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                  </button>
                </div>
              </div>

              {requiresOTP && (
                <div className="form-group">
                  <input
                    type="text"
                    className="form-input"
                    required
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit OTP"
                    autoComplete="off"
                    maxLength="6"
                  />
                  <p className="text-muted small mt-2 mb-0">
                    A verification code has been sent to your email. Please enter it above.
                  </p>
                </div>
              )}

              <div className="form-options">
                <label className="checkbox-container">
                  <input type="checkbox" className="checkbox-input" />
                  <span className="checkmark"></span>
                  Remember me
                </label>
              </div>

              <button type="submit" disabled={isLoader} className="login-button">
                {isLoader ? <Loader size="20" text="Signing" color="white" /> : 'Login'}
              </button>
            </form>
          </div>

          <p className="terms-text">
            By logging in you agree to our{' '}
            <Link to="/terms" className="terms-link">Terms of Service</Link> and{' '}
            <Link to="/privacy" className="terms-link">Privacy Policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
